import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Copy to clipboard, with a fallback and a confirmation flag.
 *
 * Two details worth keeping:
 *
 *  1. The async Clipboard API is unavailable on plain-HTTP origins and in some
 *     in-app browsers, so there is a `execCommand` fallback. Without it, copy
 *     silently does nothing for a chunk of real users.
 *  2. `copiedId` records *which* thing was copied, not just that something was.
 *     The previous version keyed this on the copied text itself, so two findings
 *     with the same fix both lit up.
 *
 * A failure is reported as `false` rather than raised, so callers can show a
 * toast instead of an `alert()`.
 */
export function useCopyToClipboard(resetAfterMs = 2000) {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const copy = useCallback(
    async (text: string, id: string): Promise<boolean> => {
      const succeeded = await writeToClipboard(text);
      if (succeeded) {
        setCopiedId(id);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopiedId(null), resetAfterMs);
      }
      return succeeded;
    },
    [resetAfterMs]
  );

  return { copy, copiedId };
}

async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied or a non-secure origin. Fall through to the fallback.
  }

  try {
    // Deprecated, but still the only option on non-secure origins.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    // Off-screen rather than `display: none`, which cannot be selected.
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.append(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}
